import { errorToast, toast } from "@/v2-ui/toast"
import Badge from "@/v2-backoffice-client/components/badge"
import { ConfirmButton } from "@/v2-backoffice-client/components/button"
import Manage from "@/v2-backoffice-client/components/manage"
import {
  ADD_ON_STANDING_VARIANT,
  addOnStandingOf,
  describeAddOnStanding,
  describeAddOnUnlocks,
} from "@/v2-backoffice-client/license/addOns/license.addOns.utils"
import { useLicenseAddOnRevokeMutation } from "@/v2-backoffice-client/license/addOns/license.addOns.revoke.mutation"
import { evictLicenseAddOnQueries } from "@/v2-backoffice-client/license/license.utils"
import type { LicenseAddOnsQuery } from "@/v2-backoffice-client/license/addOns/__types__/license.addOns.query"

export type LicenseAddOnManageHeldItemProps = {
  addOn: LicenseAddOnsQuery["getCustomerAddOns"][number]
  customerId: number
  /** The customer, by name, for the sentences. */
  who: string
  statusNameOf(code: number): string | undefined
}

// An add-on the customer already has: what it unlocks, whether it works for
// them right now (hover for why), and Remove.
function LicenseAddOnManageHeldItem(props: LicenseAddOnManageHeldItemProps) {
  const { addOn, customerId, who, statusNameOf } = props
  const standing = addOnStandingOf(addOn)

  const [revoke, { loading, error }] = useLicenseAddOnRevokeMutation({
    update: evictLicenseAddOnQueries,
    onCompleted() {
      toast({
        variant: "success",
        title: "Add-on removed",
        description: `${who} no longer has ${addOn.AddOnName}.`,
      })
    },
  })
  errorToast(error, { title: "Failed to remove the add-on" })

  return (
    <Manage.Item>
      <Manage.Item.Text
        primary={
          <>
            {addOn.AddOnName}
            {addOn.IsCustom && (
              <Badge
                size="tiny"
                variant="neutral"
                className="ml-1.5 align-middle"
                tooltip="Bespoke: composed for this customer only"
              >
                Bespoke
              </Badge>
            )}
          </>
        }
        secondary={describeAddOnUnlocks(addOn)}
        title={describeAddOnUnlocks(addOn)}
      />
      <Badge
        size="sm"
        variant={ADD_ON_STANDING_VARIANT[standing]}
        tooltip={describeAddOnStanding(addOn, who, statusNameOf)}
      >
        {standing}
      </Badge>
      <Manage.Item.Badge>Added</Manage.Item.Badge>
      <ConfirmButton
        size="xs"
        variant="destructive"
        isSquare
        iconSrc="trash"
        isLoading={loading}
        content={`Remove ${addOn.AddOnName} from ${who}`}
        confirmContent="Remove?"
        confirmVariant="destructive"
        data-feature-code="UI_BackofficeLicense_AddOns_Revoke"
        onConfirm={() => {
          revoke({ variables: { customerId, addOnId: addOn.AddOnId } })
        }}
      />
    </Manage.Item>
  )
}

export default LicenseAddOnManageHeldItem
